import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { X } from 'lucide-react';
import styles from './FilterModal.module.scss';

/**
 * Shared presentational component used by FacetSelector and LocationSelector.
 * Renders a pill row of selected values and a react-select dropdown for picking new ones.
 *
 * When `requireSearch` is true the dropdown menu is hidden until the user has
 * typed at least one character, preventing the full option list from appearing
 * on focus before any search has been entered.
 */
const FacetSelect = ({
  options,
  pills,
  placeholder,
  onSelect,
  onRemovePill,
  requireSearch,
}) => {
  const [inputValue, setInputValue] = useState('');

  return (
    <div className={styles.facetSelector}>
      {pills.length > 0 && (
        <div className={styles.pillRow}>
          {pills.map(({ key, label, isSelected = true }) => (
            <button
              key={key}
              type="button"
              className={`${styles.pill} ${
                isSelected ? styles.pillSelected : ''
              }`}
              onClick={() => onRemovePill(key)}
            >
              {label}
              {isSelected && <X size={12} className={styles.pillX} />}
            </button>
          ))}
        </div>
      )}

      <Select
        options={options}
        value={null}
        onChange={(selected) => {
          onSelect(selected);
          setInputValue('');
        }}
        inputValue={inputValue}
        onInputChange={(val, { action }) => {
          // Allow typing and blur/menu-close resets; block the reset that fires
          // when an option is selected so the menu doesn't briefly reappear
          if (action !== 'set-value') setInputValue(val);
        }}
        menuIsOpen={requireSearch ? inputValue.length > 0 : undefined}
        placeholder={placeholder}
        isClearable={false}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        classNamePrefix="facetSelect"
        className={styles.selectRoot}
        noOptionsMessage={() => 'No options found'}
        components={{
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
        }}
      />
    </div>
  );
};

FacetSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  pills: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      isSelected: PropTypes.bool,
    })
  ).isRequired,
  placeholder: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onRemovePill: PropTypes.func.isRequired,
  requireSearch: PropTypes.bool,
};

FacetSelect.defaultProps = {
  placeholder: 'Search…',
  requireSearch: false,
};

export default FacetSelect;
